import { useEffect, useState } from 'react';

import { IconArrowLeft } from '@tabler/icons-react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import {
  getPlanAndAssignmentsOptions,
  getQuizDetailsOptions,
  getQuizDetailsQueryKey,
} from '@cfa/pathway-fe-shared/xp';
import { Button, Card } from '@cfa/react-components';
import { LoadingIndicator } from '@cfa/react-core';

import { useUpdatePlanStatus } from '@/hooks/useUpdatePlanStatus';

import { itemByLanguage } from '@/utils/language';

import QuizPreviewCard from '@/components/Cards/QuizPreviewCard';
import DocumentPreview from '@/components/DocumentPreview/DocumentPreview';
import PathwayModal from '@/components/popups/PathwayModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { BaseReferenceDTO } from '@/services/content-api-types';

import { plansViewingAtom } from '../../state';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import PlanStepContainer from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/PlanStepContainer';
import Section from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/Section';

interface ArchivedPlanPreviewModalProps {
  canActivate?: boolean;
  isOpen: boolean;
  onClose: () => void;
  planId: string;
}

const ArchivedPlanPreviewModal: React.FC<ArchivedPlanPreviewModalProps> = ({
  canActivate,
  isOpen,
  onClose,
  planId,
}) => {
  const { t } = useTranslation();
  const track = useAmplitudeTrack();
  const setPlansViewing = useSetAtom(plansViewingAtom);
  const [previewDocument, setPreviewDocument] = useState<BaseReferenceDTO>();
  const [previewQuizId, setPreviewQuizId] = useState<string>();

  const {
    data: plan,
    isLoading: isPlanLoading,
    isError: isPlanError,
  } = useQuery({
    ...getPlanAndAssignmentsOptions({ path: { planId } }),
    enabled: isOpen,
    select: up => up.plan,
  });

  useEffect(() => {
    if (isPlanError) {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorLoadingPlan')}
        </ToastMessageBlock>
      ));
    }
  }, [isPlanError, t]);

  const {
    data: quizData,
    error: quizError,
    isLoading: isQuizLoading,
  } = useQuery(
    previewQuizId
      ? getQuizDetailsOptions({ path: { quizId: previewQuizId } })
      : {
          queryKey: getQuizDetailsQueryKey({
            path: { quizId: '' },
          }),
          queryFn: skipToken,
        },
  );

  const sections = plan?.content?.sections;
  const steps = plan?.content?.steps;

  const modalTitle = previewDocument
    ? previewDocument.name
    : previewQuizId
      ? itemByLanguage(quizData?.name)
      : itemByLanguage(plan?.name);

  const resetSubPreview = () => {
    setPreviewDocument(undefined);
    setPreviewQuizId(undefined);
  };

  const handleResourceOrQuizPreview = (
    documentOrQuiz: BaseReferenceDTO | string,
  ) => {
    if (typeof documentOrQuiz === 'string') {
      setPreviewQuizId(documentOrQuiz);
    } else {
      setPreviewDocument(documentOrQuiz);
    }
  };

  const activatePlanMutation = useUpdatePlanStatus(plan, {
    body: {
      status: 'ACTIVE',
    },
    // PATHWAY-4253: fire activate_plan_clicked only after status PATCH succeeds.
    onSuccess: () => {
      if (plan) {
        track(AmplitudeProductEvent.ACTIVATE_PLAN_CLICKED, {
          plan_id: plan.id,
          plan_type: plan.type,
        });
      }
      setPlansViewing('active');
      onClose();
    },
  });

  const showBackButton = !!previewDocument || !!previewQuizId;
  const showDocumentPreview = !!previewDocument;
  const showQuizLoading = !!previewQuizId && isQuizLoading;
  const showQuizPreview = !!previewQuizId && !isQuizLoading;
  const showPlanLoading = !previewDocument && !previewQuizId && isPlanLoading;
  const showPlanPreview =
    !previewDocument && !previewQuizId && !isPlanLoading && !!plan;

  return (
    <PathwayModal isOpen={isOpen} onClose={onClose}>
      <PathwayModal.Header>
        {showBackButton && (
          <div className="absolute top-4 -left-6">
            <Button
              className="-translate-2.5! pl-0!"
              color="secondary"
              onClick={resetSubPreview}
              size="md"
              variant="text"
            >
              <IconArrowLeft />
            </Button>
          </div>
        )}
        <PathwayModal.Title>{t('TrainingPlans.preview')}</PathwayModal.Title>
        <p className="text-center text-3xl">{modalTitle}</p>
      </PathwayModal.Header>
      <PathwayModal.Body noMinHeight>
        {showDocumentPreview && (
          <DocumentPreview
            previewDocument={previewDocument}
            handleGoBack={resetSubPreview}
            isTemplatePreview
          />
        )}
        {showQuizLoading && (
          <div className="flex h-full items-center justify-center py-16">
            <LoadingIndicator size="lg" />
          </div>
        )}
        {showQuizPreview && (
          <div>
            <QuizPreviewCard quiz={quizData} quizError={quizError} />
          </div>
        )}
        {showPlanLoading && (
          <div className="flex h-full items-center justify-center py-16">
            <LoadingIndicator size="lg" />
          </div>
        )}
        {showPlanPreview && (
          <div className="mx-auto flex w-full flex-col gap-6 pb-8">
            {steps?.map(step => (
              <Card className="no-hover px-4!" key={step.id}>
                <PlanStepContainer
                  className="no-drag"
                  isPreview
                  step={step}
                  handleResourceOrQuizPreview={handleResourceOrQuizPreview}
                />
              </Card>
            ))}
            {sections?.map((section, index) => (
              <Section
                index={index}
                isPreview={true}
                key={section.id}
                section={section}
                handleResourceOrQuizPreview={handleResourceOrQuizPreview}
                className="no-hover"
              />
            ))}
          </div>
        )}
      </PathwayModal.Body>
      {showPlanPreview && canActivate && (
        <PathwayModal.Footer className="py-4!">
          <Button
            className="min-w-0!"
            color="secondary"
            isPending={activatePlanMutation.isPending}
            onClick={() => {
              activatePlanMutation.mutate();
            }}
          >
            {t('Generic.activatePlan')}
          </Button>
        </PathwayModal.Footer>
      )}
    </PathwayModal>
  );
};

export default ArchivedPlanPreviewModal;
